import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader } from "@/components/Loader";
import TrustBadges from "@/components/TrustBadges";
import { Trophy, Plus, Minus, TrendingUp, Calculator, Mail, FileText, Video, HelpCircle } from "lucide-react";

/**
 * Public-facing pitch page. NO authentication — anyone with the token can view.
 * URL: /p/<token>
 *
 * Mirrors /app/pitch but uses /api/public/pitch/<token> (no auth header) so the user
 * can paste the link directly into an outbound email or LinkedIn message.
 */
const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Number(n).toFixed(0)}`;
};

// Build three concrete reasons a prospect would actually email about, each with a tailored starter body.
// The share token is appended to the subject + signature so the operator can identify which pitch
// triggered the email the moment it lands in their inbox — no email infra required.
function emailReasons(doc, token) {
    const a = doc.firm_a_name || "Firm A";
    const b = doc.firm_b_name || "your team";
    const product = doc.analysis?.product?.name || doc.product_name || "this partnership";
    const fit = doc.analysis?.fit_score || doc.fit_score || 0;
    const shortToken = (token || "").slice(0, 8);
    const sigBlock = `\n\n— sent from the ${a} × ${b} pitch [ref:${shortToken}]`;
    // Per-pitch trackable reply alias if backend supplied one; otherwise generic contact email.
    // The alias auto-threads the reply back to this exact synergy when inbound forwarding is set up.
    const target = doc.reply_email || doc.contact_email;

    const make = (subject, body) =>
        `mailto:${target}?subject=${encodeURIComponent(`${subject} [ref:${shortToken}]`)}&body=${encodeURIComponent(body + sigBlock)}`;

    return [
        {
            key: "proposal",
            icon: FileText,
            eyebrow: "Best fit",
            title: "Get a tailored proposal",
            subtitle: `Custom revenue + integration scope for ${b}`,
            href: make(
                `Tailored proposal for ${b}`,
                `Hi,\n\nThe ${a} × ${b} pitch (${fit}/100 fit) caught our attention. Could you send a tailored proposal covering:\n\n  • Recommended integration scope\n  • Revenue split / commercial terms\n  • Realistic timeline to first revenue\n\nThanks!`,
            ),
        },
        {
            key: "one-pager",
            icon: FileText,
            eyebrow: "By email",
            title: "Send the one-pager",
            subtitle: `Full ${product} brief delivered by email`,
            href: make(
                `One-pager request: ${product}`,
                `Hi,\n\nCould you email over the full one-pager for ${product} — specifically how it fits the ${b} use case in the pitch? Happy with PDF or inline.\n\nThanks!`,
            ),
        },
        {
            key: "question",
            icon: HelpCircle,
            eyebrow: "Quick reply",
            title: "Ask a question",
            subtitle: "Anything specific we should clarify?",
            href: make(
                `Question on the ${a} × ${b} pitch`,
                `Hi,\n\nQuick question on the pitch:\n\n[your question here]\n\nThanks!`,
            ),
        },
    ];
}

export default function PublicPitch() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [doc, setDoc] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        const BASE = process.env.REACT_APP_BACKEND_URL;
        // Bare axios (no interceptor — we don't want to attach an auth header for a public endpoint)
        axios.get(`${BASE}/api/public/pitch/${encodeURIComponent(token)}`)
            .then(({ data }) => setDoc(data))
            .catch((e) => {
                const d = e?.response?.data?.detail;
                setError(typeof d === "string" ? d : "Pitch not found or revoked");
            })
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) return <div className="min-h-screen bg-black p-12"><Loader label="Loading pitch" /></div>;
    if (error) return (
        <div className="min-h-screen bg-black flex items-center justify-center p-12" data-testid="public-pitch-error">
            <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-6 py-4 font-mono max-w-md text-center">
                {error}
            </div>
        </div>
    );
    if (!doc) return null;

    const a = doc.analysis || {};
    const sellingPoints = a.selling_points || [];
    const pros = a.pros_cons?.pros || [];
    const cons = a.pros_cons?.cons || [];
    const projection = a.monthly_revenue_projection || [];
    const product = a.product || { name: doc.product_name };
    const fit = a.fit_score || doc.fit_score || 0;
    const revenue = a.estimated_added_annual_revenue_usd || doc.projected_revenue || 0;

    return (
        <div className="min-h-screen bg-black text-zinc-100" data-testid="public-pitch-page">
            <div className="p-8 max-w-4xl mx-auto">
                <div className="mb-6 flex items-center justify-between print:hidden">
                    <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-amber-500">
                        powered by <span className="text-amber-500">Affynix</span>
                    </Link>
                    <button
                        onClick={() => window.print()}
                        data-testid="public-pitch-print-btn"
                        className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-amber-500 border border-zinc-800 hover:border-amber-500 px-3 py-1.5 transition-colors"
                    >
                        Save as PDF
                    </button>
                </div>

                {/* Cover */}
                <div className="border border-zinc-800 bg-zinc-950 p-8 mb-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Partnership pitch</div>
                    <h1 className="font-heading font-black tracking-tighter text-5xl text-zinc-50 mb-3 leading-none">
                        {doc.firm_a_name} <span className="text-amber-500">×</span> {doc.firm_b_name}
                    </h1>
                    {a.rationale && <p className="text-lg text-zinc-300 mt-4 leading-relaxed">{a.rationale}</p>}
                    <div className="grid grid-cols-3 gap-px bg-zinc-800 mt-8">
                        <div className="bg-zinc-950 p-4">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Fit score</div>
                            <div className="font-heading font-black text-4xl text-amber-500" data-testid="public-fit-score">{fit}<span className="text-xl text-zinc-600">/100</span></div>
                        </div>
                        <div className="bg-zinc-950 p-4">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Projected revenue</div>
                            <div className="font-heading font-black text-4xl text-emerald-400">{fmtMoney(revenue)}<span className="text-xs text-zinc-500 ml-1">/yr</span></div>
                        </div>
                        <div className="bg-zinc-950 p-4">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Product</div>
                            <div className="font-heading font-bold text-lg text-zinc-50 leading-tight">{product.name || "—"}</div>
                        </div>
                    </div>
                    {/* Three concrete reasons to email — each with a tailored starter body + token in subject for inbox tracking */}
                    {doc.contact_email && (
                        <div className="mt-6 pt-6 border-t border-zinc-800 print:hidden" data-testid="hero-contact-cta">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-1">Pick the next step</div>
                            <div className="text-sm text-zinc-400 mb-4">One click — opens your email with everything pre-written.</div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                {emailReasons(doc, token).map((r) => (
                                    <a
                                        key={r.key}
                                        href={r.href}
                                        data-testid={`hero-reason-${r.key}`}
                                        className="group flex flex-col gap-1 border border-emerald-500/40 bg-emerald-500/[0.04] hover:bg-emerald-500/10 hover:border-emerald-500 px-3 py-3 transition-colors"
                                    >
                                        <div className="flex items-center gap-2">
                                            <r.icon className="w-3.5 h-3.5 text-emerald-400" />
                                            <div className="font-mono text-[9px] uppercase tracking-wider text-emerald-400">{r.eyebrow}</div>
                                        </div>
                                        <div className="text-sm text-zinc-50 font-semibold leading-snug group-hover:text-emerald-200">{r.title}</div>
                                        <div className="text-[10px] text-zinc-500 leading-tight">{r.subtitle}</div>
                                    </a>
                                ))}
                            </div>
                            <div className="font-mono text-[9px] text-zinc-600 mt-3">
                                Or write us directly: <span className="text-zinc-400">{doc.reply_email || doc.contact_email}</span>
                                {doc.reply_email && (
                                    <span className="ml-2 inline-flex items-center gap-1 border border-emerald-500/30 bg-emerald-500/5 text-emerald-400 px-1.5 py-0.5 uppercase tracking-wider text-[8px]" data-testid="reply-tracked-pill">
                                        <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" /> Reply tracked
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Trust badges — auto-pulled from FIRM_A dossier */}
                <TrustBadges firmName={doc.firm_a_name} trust={doc.firm_a_trust} />

                {sellingPoints.length > 0 && (
                    <section className="border border-zinc-800 bg-zinc-950 mb-6">
                        <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-amber-500" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Why {doc.firm_b_name} should care</div>
                        </div>
                        <ol className="divide-y divide-zinc-800/60">
                            {sellingPoints.map((sp, i) => (
                                <li key={i} className="flex gap-4 px-5 py-4">
                                    <div className="font-mono text-xs text-amber-500 w-6 shrink-0 pt-1">{String(i + 1).padStart(2, "0")}</div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-bold text-zinc-50 text-base mb-1">{sp.point}</div>
                                        <div className="text-sm text-zinc-300 leading-relaxed">{sp.detail}</div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </section>
                )}

                {(pros.length > 0 || cons.length > 0) && (
                    <section className="grid grid-cols-2 gap-px bg-zinc-800 mb-6 border border-zinc-800">
                        <div className="bg-zinc-950 p-5">
                            <div className="flex items-center gap-2 mb-3"><Plus className="w-4 h-4 text-emerald-400" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">Pluses</div>
                            </div>
                            <ul className="space-y-2">
                                {pros.map((p, i) => <li key={i} className="text-sm text-zinc-200 leading-relaxed flex gap-2"><Plus className="w-3 h-3 text-emerald-400 mt-1 shrink-0" />{p}</li>)}
                            </ul>
                        </div>
                        <div className="bg-zinc-950 p-5">
                            <div className="flex items-center gap-2 mb-3"><Minus className="w-4 h-4 text-red-400" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-400">Minuses</div>
                            </div>
                            <ul className="space-y-2">
                                {cons.map((c, i) => <li key={i} className="text-sm text-zinc-200 leading-relaxed flex gap-2"><Minus className="w-3 h-3 text-red-400 mt-1 shrink-0" />{c}</li>)}
                            </ul>
                        </div>
                    </section>
                )}

                {/* Interactive ROI Calculator — prospects drag sliders to model their own ROI */}
                <RoiCalculator token={token} baselineRevenue={revenue} firmAName={doc.firm_a_name} firmBName={doc.firm_b_name} />

                {projection.length > 0 && (
                    <section className="border border-zinc-800 bg-zinc-950 mb-6">
                        <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-amber-500" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">12-month revenue projection (baseline)</div>
                        </div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-zinc-800 text-left">
                                    <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Month</th>
                                    <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500 text-right">Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projection.map((m, i) => (
                                    <tr key={i} className="border-b border-zinc-800/60">
                                        <td className="px-5 py-2 font-mono text-xs text-zinc-300">{m.month}</td>
                                        <td className="px-5 py-2 font-mono text-xs text-amber-500 text-right">{fmtMoney(m.revenue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="px-5 py-2 font-mono text-xs text-zinc-50 font-bold">Total</td>
                                    <td className="px-5 py-2 font-mono text-sm text-emerald-400 text-right font-bold">{fmtMoney(projection.reduce((acc, m) => acc + (Number(m.revenue) || 0), 0))}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </section>
                )}

                {/* AI-generated disclaimer — defensive lawyering for Claude-invented figures.
                    Kept low-contrast so it doesn't break the pitch narrative, but always visible. */}
                <div
                    className="border border-zinc-800 bg-zinc-950/60 px-4 py-3 mt-8 flex items-start gap-2.5 print:hidden"
                    data-testid="ai-disclaimer"
                >
                    <span className="font-mono text-[10px] text-amber-500/80 mt-0.5 shrink-0">⚠</span>
                    <div className="text-[11px] text-zinc-500 leading-relaxed">
                        <span className="font-mono uppercase tracking-wider text-zinc-400">AI-generated analysis.</span>{" "}
                        Figures, financials, and competitive claims are synthesized by Claude Sonnet 4.5 from public
                        signals — they are estimates, not audited data. Verify any specific number before contracting.{" "}
                        <Link
                            to="/methodology"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="methodology-link"
                            className="text-amber-500 hover:text-amber-400 underline-offset-2 hover:underline"
                        >
                            How we build dossiers →
                        </Link>
                    </div>
                </div>

                <div className="text-center font-mono text-[10px] text-zinc-700 mt-4 print:hidden">
                    Pitch shared by {doc.firm_a_name}. Want to build your own? Visit <Link to="/" className="text-amber-500 hover:text-amber-400">Affynix</Link>.
                </div>
            </div>

            {/* Sticky bottom CTA — surfaces the proposal request (best signal) to anyone still scrolling. */}
            {doc.contact_email && (
                <div className="sticky bottom-0 left-0 right-0 border-t border-emerald-500/40 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/80 print:hidden" data-testid="sticky-contact-cta">
                    <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs text-zinc-300 leading-snug min-w-0">
                            <span className="font-mono text-emerald-400 text-[10px] uppercase tracking-wider">Like what you see?</span>{" "}
                            <span className="text-zinc-400">Get a tailored proposal in your inbox — one click.</span>
                        </div>
                        <a
                            href={emailReasons(doc, token)[0].href}
                            data-testid="sticky-contact-link"
                            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-[11px] uppercase tracking-wider px-4 py-2 shrink-0"
                        >
                            <Mail className="w-3.5 h-3.5" /> Request proposal
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
}

// Interactive ROI calculator — embedded in the public pitch.
// Prospects drag 3 sliders to model their own scenario. Each drag is debounce-logged
// to /api/public/pitch/{token}/interact so the pitch owner sees engagement DEPTH.
function RoiCalculator({ token, baselineRevenue, firmAName, firmBName }) {
    // Sensible defaults — derived from the baseline so the calculator starts in a believable state
    const initialCustomers = 5000;
    const initialDeal = 1200; // $ ASP per customer per year
    const initialAdoption = 18; // %

    const [customers, setCustomers] = useState(initialCustomers);
    const [deal, setDeal] = useState(initialDeal);
    const [adoption, setAdoption] = useState(initialAdoption);
    const timers = useRef({});

    // Real-time computed Y1 / Y3 revenue
    const yearOne = useMemo(() => customers * deal * (adoption / 100), [customers, deal, adoption]);
    const yearThree = useMemo(() => yearOne * 2.1, [yearOne]); // 2.1x growth multiplier — conservative

    const upliftVsBaseline = baselineRevenue > 0 ? Math.round(((yearOne - baselineRevenue) / baselineRevenue) * 100) : 0;

    // Debounced telemetry — only fire once the user stops dragging for 600ms per slider
    const logInteraction = (slider, value, computed) => {
        if (timers.current[slider]) clearTimeout(timers.current[slider]);
        timers.current[slider] = setTimeout(() => {
            const BASE = process.env.REACT_APP_BACKEND_URL;
            // bare axios — no auth header for public endpoint
            axios.post(`${BASE}/api/public/pitch/${encodeURIComponent(token)}/interact`, {
                slider,
                value,
                computed_revenue: computed,
            }).catch(() => { /* silent */ });
        }, 600);
    };

    const onChange = (slider, value, setter) => {
        setter(value);
        // Compute the projected revenue AT this slider value (so we capture intent)
        const next = {
            customer_count: { customers: value, deal, adoption },
            deal_size: { customers, deal: value, adoption },
            adoption_rate: { customers, deal, adoption: value },
        }[slider];
        if (next) {
            const projected = next.customers * next.deal * (next.adoption / 100);
            logInteraction(slider, value, projected);
        }
    };

    return (
        <section className="border border-emerald-500/30 bg-gradient-to-br from-zinc-950 to-emerald-950/10 mb-6 print:hidden" data-testid="roi-calculator">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2 flex-wrap">
                <Calculator className="w-4 h-4 text-emerald-400" />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">Model your own ROI</div>
                <div className="ml-auto font-mono text-[9px] text-zinc-500">Drag to estimate · live</div>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sliders */}
                <div className="space-y-5">
                    <SliderRow
                        label={`${firmBName || "Your"} customer base`}
                        testid="slider-customers"
                        value={customers}
                        min={500}
                        max={100000}
                        step={500}
                        format={(v) => `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}K customers`}
                        onChange={(v) => onChange("customer_count", v, setCustomers)}
                    />
                    <SliderRow
                        label="Average deal size / yr"
                        testid="slider-deal"
                        value={deal}
                        min={50}
                        max={50000}
                        step={50}
                        format={(v) => `$${v.toLocaleString()}`}
                        onChange={(v) => onChange("deal_size", v, setDeal)}
                    />
                    <SliderRow
                        label="Adoption rate of partnership"
                        testid="slider-adoption"
                        value={adoption}
                        min={1}
                        max={100}
                        step={1}
                        format={(v) => `${v}%`}
                        onChange={(v) => onChange("adoption_rate", v, setAdoption)}
                    />
                </div>

                {/* Live result */}
                <div className="border border-zinc-800 bg-black p-5" data-testid="roi-result">
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Year 1 revenue uplift</div>
                    <div className="font-heading font-black text-4xl text-emerald-400 leading-none mb-1" data-testid="roi-yr1">
                        ${yearOne.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    {baselineRevenue > 0 && (
                        <div className={`font-mono text-[10px] mb-4 ${upliftVsBaseline >= 0 ? "text-emerald-500" : "text-amber-500"}`} data-testid="roi-uplift">
                            {upliftVsBaseline >= 0 ? "+" : ""}{upliftVsBaseline}% vs baseline projection
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-zinc-800">
                        <div>
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Year 3 (2.1× ramp)</div>
                            <div className="font-heading font-black text-xl text-zinc-50" data-testid="roi-yr3">
                                ${yearThree.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                        </div>
                        <div>
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">3-yr total</div>
                            <div className="font-heading font-black text-xl text-amber-400" data-testid="roi-total">
                                ${(yearOne + yearOne * 1.45 + yearThree).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 font-mono text-[9px] text-zinc-600 leading-relaxed">
                        Estimate: <span className="text-zinc-400">{customers.toLocaleString()}</span> customers × <span className="text-zinc-400">${deal.toLocaleString()}</span> ASP × <span className="text-zinc-400">{adoption}%</span> adoption.
                    </div>
                </div>
            </div>

            <div className="px-5 pb-4 -mt-1 font-mono text-[9px] text-zinc-600 italic">
                Numbers are directional — actual outcomes depend on integration depth and go-to-market. {firmAName} can share the underlying assumptions on request.
            </div>
        </section>
    );
}

function SliderRow({ label, value, min, max, step, format, onChange, testid }) {
    return (
        <div data-testid={`${testid}-row`}>
            <div className="flex items-center justify-between mb-1.5">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</label>
                <span className="font-mono text-xs text-emerald-400 font-bold" data-testid={`${testid}-value`}>{format(value)}</span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                data-testid={testid}
                className="w-full accent-emerald-500 cursor-pointer"
            />
        </div>
    );
}

